import { useMemo, useCallback } from "react";
import moment from "moment-timezone";
import { Clock } from "lucide-react";
import { Progress } from "../../../../../../../components/ui/progress";
import Checkbox from "../../../../../../../components/shared/inputs/Checkbox";
import CurrentTime from "../../CurrentTime";
import { useTime } from "../../../../../../../utils/hooks/time";
import { useCompanyCurrency, useCompanyDateFormat } from "../../../../../../../utils/hooks/company";
import { useFilters } from "../../../../../../../utils/hooks/useFilters";
import { useMenuContext } from "../../../../hooks/context/useMenuContext";
import { useScheduleContext } from "../../../../hooks/context/useScheduleContext";
import { usePermissions } from "../../../../hooks/usePermissions";
import { renderDurationAsFormat } from "../../../../../../../utils/helpers/date";
import { currencyFormatter } from "../../../../../../../utils/helpers/currencyFormatter";
import { SEGMENT_COLORS, SEGMENT_TYPES } from "../../../../constants/segmentColors";
import { cn } from "../../../../../../../lib/utils";
import type { Event } from "../../../../types/Event";

function DateCell({ day, cellIndex, cellWidth, currentTimeOffset }: any) {
    const now = useTime();
    const cellStart = moment(day);
    const cellEnd = cellStart.clone().add(1, "hour");
    const dateFormat = useCompanyDateFormat();
    const currency = useCompanyCurrency();
    const [{ mode }] = useFilters();
    const { openMenu } = useMenuContext();
    const { ctrlShiftS, events, unassignedEvents, selectedEvents, showCurrentTime, dispatch, type } = useScheduleContext();
    const isTeamScheduler = ["team", "template", "profile"].includes(type);
    const { canViewRates } = usePermissions();

    const isCurrentTime = useMemo(() => {
        if (!showCurrentTime || !["team", "home"].includes(type)) {
            return false;
        } else {
            return mode === "day"
                ? moment(now).isBetween(cellStart, cellEnd)
                : moment(now).isSame(cellStart, "day");
        }
    }, [showCurrentTime, type, mode, now, cellStart, cellEnd]);

    const renderValue: string = useMemo(() => {
        if (mode === "day") {
            return moment(day).format("ha");
        } else {
            if (["team", "home"].includes(type)) {
                return moment(day).format(dateFormat || "YYYY-MM-DD")
            } else {
                return moment(day).format("dddd");
            }
        }
    }, [mode, dateFormat, type, day]);

    const isSameDay = useCallback(
        (date: string) => moment(date).isSame(day, "day"),
        [day]
    );

    const dailyEvents = useMemo(
        () => events.filter((event: any) => isSameDay(event.startDate) && event?.type !== "unavailable"),
        [events, isSameDay]
    );

    const dailyUnassignedEvents = useMemo(
        () => unassignedEvents.filter((event: any) => isSameDay(event.startDate)),
        [unassignedEvents, isSameDay]
    );

    const { dailySelectedEvents, dailySelectedUnassignedEvents } = useMemo(() => {
        const daily = selectedEvents.filter((event: any) => isSameDay(event.startDate) && event?.type !== "unavailable");
        return {
            dailySelectedEvents: daily.filter((event: any) => event.resourceId !== "unassigned-shifts"),
            dailySelectedUnassignedEvents: daily.filter((event: any) => event.resourceId === "unassigned-shifts"),
        };
    }, [selectedEvents, isSameDay]);

    const isChecked = useMemo(() => {
        const allAssignedSelected =
            dailyEvents.length > 0 &&
            dailyEvents.every((event: any) =>
                dailySelectedEvents.some((sel: any) => sel.id === event.id)
            );

        const allUnassignedSelected =
            dailyUnassignedEvents.length > 0 &&
            dailyUnassignedEvents.every((event: any) =>
                dailySelectedUnassignedEvents.some((sel: any) => sel.id === event.id)
            );

        return allAssignedSelected || allUnassignedSelected;
    }, [
        dailyEvents,
        dailyUnassignedEvents,
        dailySelectedEvents,
        dailySelectedUnassignedEvents,
    ]);

    const onChange = useCallback(() => {
        const hasUnassignedSelected = selectedEvents.some(
            (event: any) => event.resourceId === "unassigned-shifts"
        );

        const payload = (hasUnassignedSelected ||
            (dailyEvents?.length === 0 && selectedEvents?.length === 0)
        ) ? dailyUnassignedEvents : dailyEvents;
        const ids = payload.map((item: any) => item.id);
        const values = isChecked
            ? selectedEvents.filter((event: Event) => !ids.includes(event.id))
            : [...selectedEvents, ...payload];

        dispatch({ type: "select-events", payload: values });
    }, [selectedEvents, dailyEvents, dailyUnassignedEvents, isChecked, dispatch]);

    const onContextMenu = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "my-profile") return;
        openMenu({
            id: "day-cell-menu",
            x: e.clientX,
            y: e.clientY + 10,
            selected: day
        });
    }, [openMenu, day, type]);

    const { total, durationByType, totalDuration } = useMemo(() => {
        const byType: Record<string, number> = {};
        let totalCost = 0;
        let duration = 0;

        (dailyEvents || []).forEach((event: any) => {
            totalCost += parseFloat(event.total || "0");
            (event.sections || []).forEach((section: any) => {
                const type = section.type in SEGMENT_COLORS ? section.type : "regular";
                const value = parseFloat(section.duration || "0");
                byType[type] = (byType[type] ?? 0) + value;
                duration += value;
            });
        });

        return { total: totalCost, durationByType: byType, totalDuration: duration };
    }, [dailyEvents]);

    const segments = useMemo(() => {
        if (totalDuration === 0) return [];
        const MIN_PCT = 7;
        const raw = SEGMENT_TYPES
            .filter((type) => (durationByType[type] ?? 0) > 0)
            .map((type) => ({
                value: (durationByType[type] / totalDuration) * 100,
                color: SEGMENT_COLORS[type],
            }));
        const boostedSum = raw.reduce((sum: number, item: { value: number }) => sum + Math.max(item.value, MIN_PCT), 0);
        const scale = 100 / boostedSum;
        return raw.map((row: { value: number; color: string }) => ({
            ...row,
            value: Math.max(row.value, MIN_PCT) * scale
        }));
    }, [durationByType, totalDuration]);

    return (
        <div
            className={cn(
                "day-header tw-min-h-14 tw-py-2",
                mode !== "day" && "tw-cursor-pointer tw-min-h-[5.75rem]",
                isCurrentTime && "tw-bg-primary/5",
                !["team", "home"].includes(type) && "tw-min-h-10"
            )}
            style={{
                minWidth: `${cellWidth}px`,
                position: "relative",
            }}
            onContextMenu={mode !== "day" ? onContextMenu : undefined}
        >
            <div className="tw-flex tw-items-center align-center tw-justify-center tw-gap-2 tw-w-full">
                {((mode !== "day") && ctrlShiftS) && (
                    <Checkbox
                        id={day}
                        className="tw-bg-white"
                        checked={isChecked}
                        onCheckedChange={onChange}
                        disabled={dailyEvents?.length === 0}
                    />
                )}
                <div className="tw-flex tw-flex-col tw-items-center tw-min-w-0 tw-flex-0">
                    <span className="tw-text-sm tw-text-foreground">
                        {renderValue}
                    </span>
                    {(mode !== "day" && ["team", "home"].includes(type)) && (
                        <>
                            <div className="tw-flex tw-items-center tw-text-xs tw-gap-2 tw-my-2">
                                <div className="tw-text-xs tw-flex tw-items-center tw-gap-1">
                                    <Clock size={12} />{renderDurationAsFormat(totalDuration, "HH:mm")}
                                </div>
                                {(canViewRates || !isTeamScheduler) && (
                                    <div>
                                        {currencyFormatter(total, currency)}
                                    </div>
                                )}
                            </div>
                            <Progress segments={segments} className="tw-h-1.5 tw-w-24" />
                        </>
                    )}
                </div>
            </div>

            <CurrentTime
                header={true}
                currentTimeOffset={currentTimeOffset}
                cellIndex={cellIndex}
                cellWidth={cellWidth}
            />
        </div>
    );
}

export default DateCell;
